/**
 * The plan panel — the first thing on the page and the only block that answers
 * the question an admin actually opens Billing with: *what is this going to
 * cost me this month?*
 *
 * Everything else on the page is evidence for this panel. It states the plan,
 * burns the page allowance down against the cycle, and restates the position
 * as a projection, because "3,100 of 5,000 pages on the 17th" only means
 * something once it reads "on track for 5,471".
 */
import { useEffect, useState } from 'react'
import { ArrowRight, Gem, Layers, Sparkles, TrendingUp } from 'lucide-react'
import { Card, CountUp, HelpTip, usePrefersReducedMotion } from './BillingPrimitives'
import { billingMonthTooltip, formatCycleLabel, formatDaysLeft, formatInt, formatMoneyAs } from '../../utils/billing'

const PLAN_ICONS = { starter: Sparkles, scale: Layers, enterprise: Gem }

/**
 * Per-tier identity gradient for the plan panel's left column.
 *
 * Each tier keeps a distinct hue matching its icon, so the panel differentiates
 * plans at a glance rather than every tier rendering the same navy slab. Light
 * and dark are tuned separately rather than one gradient with a dark: opacity
 * knob — a light gradient just turned translucent goes flat against a dark
 * card (that was the bug: navy-900/20 on a near-navy dark surface reads as no
 * gradient at all). Dark instead lifts a touch of hue into the gray-900 base,
 * enough to read as tinted without glowing.
 */
const PLAN_GRADIENTS = {
  starter: 'bg-gradient-to-br from-amber-100/80 via-amber-50/40 to-transparent ' +
           'dark:from-amber-500/[0.12] dark:via-amber-500/[0.04] dark:to-transparent',
  // The brand's own navy-400 token is deliberately desaturated for dark-mode
  // text/UI use (see index.css), which made this tier's tint read far weaker
  // than the amber/purple accents above once portalled onto a near-black card.
  // A plain Tailwind blue keeps the same "cool, corporate" identity at a
  // saturation that actually survives at low opacity.
  scale: 'bg-gradient-to-br from-navy-100/80 via-navy-50/40 to-transparent ' +
         'dark:from-blue-400/[0.14] dark:via-blue-400/[0.05] dark:to-transparent',
  enterprise: 'bg-gradient-to-br from-purple-100/80 via-purple-50/40 to-transparent ' +
              'dark:from-purple-400/[0.14] dark:via-purple-400/[0.05] dark:to-transparent',
}

/**
 * Allowance burn-down.
 *
 * The track is scaled to whichever is larger — the allowance or where the
 * cycle is heading — so an overage renders as bar *past* the allowance marker
 * rather than as a full bar that cannot show how far past it is.
 */
function QuotaMeter({ used, included, projected, tone, unlimited = false, overageBilled = true }) {
  const reduced = usePrefersReducedMotion()
  const [mounted, setMounted] = useState(reduced)

  useEffect(() => {
    if (reduced) return
    const id = requestAnimationFrame(() => setMounted(true))
    return () => cancelAnimationFrame(id)
  }, [reduced])

  // Unlimited (internal / contract plans): no allowance to measure against,
  // so the track is scaled to where the cycle is heading and nothing is overage.
  const scaleMax = unlimited ? Math.max(projected, used, 1) : Math.max(included, projected, used, 1)
  const pctOf = v => (mounted ? Math.min(100, (v / scaleMax) * 100) : 0)

  const withinAllowance = unlimited ? used : Math.min(used, included)
  const usedOverage = unlimited ? 0 : Math.max(0, used - included)
  const projectedTail = Math.max(0, projected - used)

  const fill = {
    good: 'bg-emerald-500 dark:bg-emerald-400',
    warning: 'bg-amber-500 dark:bg-amber-400',
    critical: 'bg-rose-500 dark:bg-rose-400',
    neutral: 'bg-gray-400 dark:bg-gray-500',
    over_free: 'bg-gray-400 dark:bg-gray-500',
  }[tone]

  return (
    <div className="mt-4">
      <div
        className="relative h-3 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden"
        role="meter"
        aria-valuenow={used}
        aria-valuemin={0}
        aria-valuemax={scaleMax}
        aria-label={unlimited
          ? `${formatInt(used)} pages used, unlimited allowance`
          : `${formatInt(used)} of ${formatInt(included)} included pages used`}
      >
        {/* pages already processed, inside the allowance */}
        <div
          className="absolute inset-y-0 left-0 bg-navy-600 dark:bg-navy-400 rounded-full transition-[width] duration-[900ms] ease-out"
          style={{ width: `${pctOf(withinAllowance)}%` }}
        />
        {/* pages already processed, beyond the allowance: billed overage, unless the plan prices it at 0 */}
        {usedOverage > 0 && (
          <div
            className={`absolute inset-y-0 ${overageBilled ? 'bg-rose-500 dark:bg-rose-400' : 'bg-gray-400 dark:bg-gray-500'}
                        transition-[width,left] duration-[900ms] ease-out`}
            style={{ left: `${pctOf(included)}%`, width: `${pctOf(usedOverage)}%` }}
          />
        )}
        {/* where the cycle lands at the current run rate */}
        {projectedTail > 0 && (
          <div
            className={`absolute inset-y-0 billing-hatch transition-[width,left] duration-[900ms] delay-150 ease-out ${fill} opacity-40`}
            style={{ left: `${pctOf(used)}%`, width: `${pctOf(projectedTail)}%` }}
          />
        )}
        {/* the allowance itself */}
        {!unlimited && projected > included && (
          <div
            className="absolute inset-y-0 w-0.5 bg-gray-900 dark:bg-gray-100 transition-[left] duration-[900ms] ease-out"
            style={{ left: `${pctOf(included)}%` }}
          />
        )}
      </div>

      <div className="flex items-center justify-between mt-2 text-[11px] text-gray-500 dark:text-gray-400">
        <span className="inline-flex items-center gap-3 flex-wrap">
          <span className="inline-flex items-center gap-1.5">
            <i className="w-2 h-2 rounded-sm bg-navy-600 dark:bg-navy-400" /> Used
          </span>
          <span className="inline-flex items-center gap-1.5">
            <i className={`w-2 h-2 rounded-sm billing-hatch opacity-60 ${fill}`} /> Projected
          </span>
          {!unlimited && projected > included && (
            <span className="inline-flex items-center gap-1.5">
              <i className="w-0.5 h-2.5 bg-gray-900 dark:bg-gray-100" /> Allowance
            </span>
          )}
        </span>
        <span className="tabular-nums">{unlimited ? '∞ included' : `${formatInt(included)} included`}</span>
      </div>
    </div>
  )
}

function Figure({ label, value, help, tone = '' }) {
  return (
    <div className="min-w-0">
      <p className="text-[11px] text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
        {label}
        {help && <HelpTip>{help}</HelpTip>}
      </p>
      <p className={`text-[15px] font-semibold tabular-nums mt-0.5 ${tone || 'text-gray-900 dark:text-gray-100'}`}>
        {value}
      </p>
    </div>
  )
}

/**
 * Who bills the extra pages, and whether the figures are the bill or an
 * estimate (docs/BILLING_DESIGN.md). Says nothing it can't back: with metering
 * off and the ledger readable there is nothing to add.
 */
export function BillingStatus({ plan }) {
  const mp = plan?.marketplace
  const lines = []
  if (plan?.ledger_catching_up) {
    lines.push({ tone: 'info', text: 'Billing records are being prepared from your processing history, so these figures are an estimate for now.' })
  } else if (plan?.usage_source === 'estimate') {
    lines.push({ tone: 'warn', text: "Billing records couldn't be read just now, so these figures are an estimate from processing jobs." })
  }
  if (mp?.mode === 'live') {
    lines.push({ tone: 'info', text: `Extra pages are billed by Microsoft on your Azure invoice. Reported this billing month: ${formatInt(mp.reported_pages)} pages.` })
  } else if (mp?.mode === 'shadow') {
    lines.push({ tone: 'info', text: `Extra pages are being calculated but not billed yet. Calculated this billing month: ${formatInt(mp.shadow_pages)} pages.` })
  }
  if (mp && mp.mode !== 'off' && mp.problems > 0) {
    lines.push({ tone: 'warn', text: `${formatInt(mp.problems)} hour${mp.problems === 1 ? '' : 's'} of usage couldn't be reported to Microsoft this billing month.` })
  }
  if (!lines.length) return null
  return (
    <div data-testid="billing-status" className="mt-4 space-y-1">
      {lines.map(line => (
        <p key={line.text} className={`text-[11px] ${line.tone === 'warn'
          ? 'text-amber-700 dark:text-amber-400' : 'text-gray-500 dark:text-gray-400'}`}>
          {line.text}
        </p>
      ))}
    </div>
  )
}

export default function PlanSummary({ plan, onCompare, displayCurrency }) {
  const usage = plan?.usage || {}
  const cycle = plan?.cycle || {}
  const currency = plan?.currency || { code: 'GBP', symbol: '£' }
  const fmt = (value, opts) => formatMoneyAs(value, currency, displayCurrency, opts)

  // Internal and contract plans: same panel, unlimited allowance, no price.
  const unlimited = plan?.unlimited === true
  const projectedPct = usage.projected_percent || 0
  // A run rate needs a few days of the cycle behind it: one big upload on day 1
  // projects to a month of the same. Until then the projection is shown but
  // not judged (an older backend sends no flag; only an explicit false counts).
  const reliable = usage.projection_reliable !== false
  const overNow = (usage.pages_included || 0) > 0 && (usage.pages_used || 0) > usage.pages_included
  // A plan that prices overage at 0 (a private plan with its fee and overage
  // overridden) charges nothing past the allowance, so passing it is stated
  // neutrally rather than flagged as an overage.
  const freeOverage = !unlimited && plan?.overage_per_page != null && Number(plan.overage_per_page) === 0
  const tone = unlimited
    ? 'good'
    : freeOverage && (overNow || (reliable && projectedPct > 100))
      ? 'over_free'
      : !reliable
        ? (overNow ? 'critical' : 'neutral')
        : projectedPct > 100 ? 'critical' : projectedPct >= 85 ? 'warning' : 'good'
  const Icon = PLAN_ICONS[plan?.id] || Layers

  // No plan configured for this workspace: say so, rather than pricing the
  // usage against a plan nobody bought.
  if (plan?.configured === false) {
    return (
      <Card data-tour="plan" className="overflow-hidden animate-card-in" style={{ animationDelay: '40ms' }}>
        <div className="p-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <span className="text-[10px] font-semibold uppercase tracking-[0.09em] text-gray-500 dark:text-gray-400">
              Your plan
            </span>
            <h2 className="mt-1 text-xl font-bold text-gray-900 dark:text-gray-100">No plan set up yet</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {formatInt(usage.pages_used || 0)} pages processed this billing month. Your plan and its
              allowance will show here once it is set up for this workspace.
            </p>
          </div>
          <button
            type="button"
            onClick={onCompare}
            className="inline-flex items-center gap-1.5 text-[13px] font-semibold
                       text-navy-700 dark:text-navy-300 hover:gap-2.5 transition-all
                       focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500 rounded"
          >
            View plans
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </Card>
    )
  }

  const status = {
    good: { label: unlimited ? 'Unlimited' : 'On track', cls: 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400' },
    warning: { label: 'Nearing allowance', cls: 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400' },
    critical: {
      label: reliable ? 'Overage projected' : 'Over allowance',
      cls: 'bg-rose-50 dark:bg-rose-900/20 text-rose-700 dark:text-rose-400',
    },
    neutral: { label: 'Too early to project', cls: 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300' },
    over_free: {
      label: overNow ? 'Over allowance, no extra charge' : 'Projected over allowance, no extra charge',
      cls: 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300',
    },
  }[tone]

  return (
    <Card data-tour="plan" className="overflow-hidden animate-card-in" style={{ animationDelay: '40ms' }}>
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,320px)_1fr]">
        {/* ---- identity: which plan, at what price ---- */}
        <div className={`p-5 lg:border-r border-b lg:border-b-0 border-gray-200 dark:border-gray-800
                        ${PLAN_GRADIENTS[plan?.id] || PLAN_GRADIENTS.scale}`}>
          <div className="flex items-center gap-2">
            <span className="p-1.5 rounded-lg bg-navy-600/10 dark:bg-navy-400/10 text-navy-700 dark:text-navy-300">
              <Icon className="w-4 h-4" />
            </span>
            <span className="text-[10px] font-semibold uppercase tracking-[0.09em] text-gray-500 dark:text-gray-400">
              Your plan
            </span>
          </div>

          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{plan?.name || 'Plan'}</h2>
            {plan?.customised && (
              <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
                Custom terms
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{plan?.tagline}</p>

          <div className="mt-4 flex items-baseline gap-1.5">
            <span className="text-3xl font-bold text-gray-900 dark:text-gray-100 tabular-nums">
              {plan?.monthly_fee == null ? plan?.price_label : fmt(plan?.monthly_fee, { pence: false })}
            </span>
            {plan?.monthly_fee != null && <span className="text-xs text-gray-500 dark:text-gray-400">/ month</span>}
          </div>
          <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
            {unlimited
              ? 'Unlimited pages, no overage'
              : freeOverage
                ? <>Includes {formatInt(usage.pages_included)} pages, no charge beyond that</>
                : <>Includes {formatInt(usage.pages_included)} pages, then{' '}{fmt(plan?.overage_per_page)} per page</>}
          </p>

          {/* Internal and contract plans are not bought from the price list. */}
          {plan?.billable !== false && (
            <button
              type="button"
              onClick={onCompare}
              className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-semibold
                         text-navy-700 dark:text-navy-300 hover:gap-2.5 transition-all
                         focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500 rounded"
            >
              Compare plans
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {/* ---- position: where this cycle stands and where it is going ---- */}
        <div className="p-5">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <p className="text-[11px] text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
                Pages this billing month
                <HelpTip>
                  Every page analysed in the current billing month, counted once per completed processing
                  job. Failed jobs and template set-up uploads aren't counted. Pages are what your allowance
                  is measured in.
                </HelpTip>
              </p>
              <p className="mt-1 flex items-baseline gap-2">
                <CountUp
                  value={usage.pages_used || 0}
                  className="text-4xl font-bold text-gray-900 dark:text-gray-100 tracking-tight"
                />
                <span className="text-sm text-gray-400 dark:text-gray-500 tabular-nums">
                  / {unlimited ? '∞' : formatInt(usage.pages_included)}
                </span>
              </p>
            </div>
            <div className="text-right">
              <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full ${status.cls}`}>
                <TrendingUp className="w-3 h-3" />
                {status.label}
              </span>
              <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1.5">
                <span title={billingMonthTooltip(cycle)} className="cursor-help underline decoration-dotted underline-offset-2">
                  {formatCycleLabel(cycle)}
                </span>
                {' '}· {formatDaysLeft(cycle.days_remaining)}
              </p>
            </div>
          </div>

          <div data-tour="meter">
          <QuotaMeter
            used={usage.pages_used || 0}
            included={usage.pages_included || 0}
            projected={usage.projected_pages || 0}
            tone={tone}
            unlimited={unlimited}
            overageBilled={!freeOverage}
          />
          </div>

          <div data-tour="forecast" className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-5 pt-4 border-t border-gray-100 dark:border-gray-800">
            <Figure
              label="Projected pages"
              value={formatInt(usage.projected_pages)}
              help="Where this billing month ends if the remaining days look like the days so far: a straight-line run rate, not a forecast model."
            />
            <Figure
              label="Projected overage"
              value={unlimited ? 'None' : fmt(usage.projected_overage_cost)}
              tone={!unlimited && reliable && usage.projected_overage_cost > 0 ? 'text-amber-600 dark:text-amber-400' : ''}
              help={unlimited
                ? 'Your plan has no page allowance, so there is never an overage.'
                : freeOverage
                  ? 'Pages beyond your allowance carry no extra charge on this plan.'
                  : `Pages beyond your allowance are charged at ${fmt(plan?.overage_per_page)} each.`}
            />
            <Figure
              label="Projected total"
              value={unlimited ? plan?.price_label : fmt(usage.projected_total_cost)}
              help={unlimited
                ? 'Not billed through Microsoft Marketplace.'
                : 'Base fee plus projected overage. Your Azure Marketplace invoice is the authority; this is an estimate from usage recorded so far.'}
            />
          </div>
          <BillingStatus plan={plan} />
        </div>
      </div>
    </Card>
  )
}
